import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { apiClient, setSessionToken, clearTokens, isPendingOAuthToken, getAccessToken } from '@/api/client';
import posthog from 'posthog-js';

function getUserIdFromToken(): string | null {
  try {
    const token = getAccessToken();
    if (!token) return null;
    const payload = token.split('.')[1];
    if (!payload) return null;
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { user_id?: number };
    const id = claims.user_id;
    return id && id !== 0 ? String(id) : null;
  } catch {
    return null;
  }
}

type AuthContextValue = {
  isAuthenticated: boolean;
  isLoading: boolean;
  isProfileComplete: boolean;
  userRole: string;
  user: any | null;
  refetch: () => Promise<void>;
  setAuthenticated: (value: boolean) => void;
  setProfileComplete: (value: boolean) => void;
  setUserRole: (role: string) => void;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setAuthenticatedState] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [userRole, setUserRole] = useState<string>('freelancer');
  const [user, setUser] = useState<any | null>(null);

  // Mirrors isAuthenticated for refetch()'s catch handler, which is memoized
  // with an empty dep array and would otherwise close over a permanently
  // stale (initial-render) value of isAuthenticated.
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  // Guards against out-of-order responses: a slow/cold-started request
  // (Fly scale-to-zero wake-up) can resolve AFTER a newer refetch() already
  // set correct state, silently clobbering it back to "logged out" or
  // "profile incomplete". Only the response from the most recently issued
  // refetch() call is allowed to update state.
  const requestIdRef = useRef(0);

  const refetch = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    try {
      const res = await apiClient.get('/users/me');
      if (requestIdRef.current !== requestId) return; // superseded by a newer refetch()
      const apiData = res.data?.data || res.data;

      // Backend response structure:
      // - Profile exists: apiData IS the User object directly
      // - Profile doesn't exist: apiData = {profile: null, user_id: X}
      const profile = apiData?.profile !== undefined ? apiData.profile : apiData;

      // Profile is complete ONLY if user_name exists (required field)
      const profileComplete = !!(profile && profile !== null && profile.user_name);
      const role = profile?.role || 'freelancer';
      setUserRole(role);
      setUser(profile);
      setAuthenticatedState(true);
      setIsProfileComplete(profileComplete);
      const userId = getUserIdFromToken();
      if (userId) {
        posthog.identify(userId, { role });
      }
    } catch (err: any) {
      if (requestIdRef.current !== requestId) return; // superseded by a newer refetch()
      // A brand-new Google/LinkedIn signup holds a pending-OAuth token
      // (user_id: 0) with no real user row yet, so this app-wide /users/me
      // check ALWAYS 401s mid-signup. This provider mounts fresh on the
      // OAuth redirect landing and races SignUp.tsx's code exchange; clearing
      // the token here wipes the still-valid pending session out from under
      // the profile-completion step. The interceptor already refuses to
      // clear it — this catch must not undo that.
      if (isPendingOAuthToken()) {
        return;
      }
      if (err.message === 'Network Error' || err.response?.status >= 500) {
        console.warn("Auth check failed (Server 502 or CORS). App might be in offline mode.", err);
        // Don't fully reset state yet if we have a token, to avoid flickering/redirects
        if (isAuthenticatedRef.current) return;
      }
      setAuthenticatedState(false);
      setIsProfileComplete(false);
      setSessionToken(null);
    } finally {
      if (requestIdRef.current === requestId) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // ── Cross-tab sync via BroadcastChannel ────────────────────────────────────
  // When another tab logs in or out, re-validate so this tab stays in sync.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.BroadcastChannel) return;
    const channel = new BroadcastChannel('auth');
    channel.onmessage = (e) => {
      if (e.data === 'logout') {
        setSessionToken(null);
        setAuthenticatedState(false);
        setIsProfileComplete(false);
        setIsLoading(false);
      } else if (e.data === 'login') {
        refetch();
      }
    };
    return () => channel.close();
  }, [refetch]);

  const setAuthenticated = useCallback((value: boolean) => {
    setAuthenticatedState(value);
    setIsLoading(false);
  }, []);

  const setProfileComplete = useCallback((value: boolean) => {
    setIsProfileComplete(value);
  }, []);

  const logout = useCallback(async () => {
    posthog.capture('user_logged_out');
    posthog.reset();
    clearTokens();
    setAuthenticatedState(false);
    setIsProfileComplete(false);
    try {
      await apiClient.post('/auth/logout');
      if (typeof window !== 'undefined' && window.BroadcastChannel) {
        new BroadcastChannel('auth').postMessage('logout');
      }
    } catch {
      // Cookie may already be gone or network error; auth state is cleared
    }
  }, []);

  const value: AuthContextValue = {
    isAuthenticated,
    isLoading,
    isProfileComplete,
    userRole,
    user,
    refetch,
    setAuthenticated,
    setProfileComplete,
    setUserRole,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
